import React, { useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { motion, useInView } from 'framer-motion';
import { 
  Building2, Users, FileText, Calendar, Scale, CreditCard, 
  CheckCircle, ArrowRight, Star, Play, Shield, Clock, 
  Zap, TrendingUp, MessageCircle, ChevronRight, Briefcase, Phone,
  Gift, Unlock, Heart, Mail, AlertTriangle, FileCheck
} from 'lucide-react';

// Animation variants pour framer-motion
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

const slideFromLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const slideFromRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Composant réutilisable pour les sections animées
const AnimatedSection = ({ children, className = "", variants = fadeInUp, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      style={{ transitionDelay: `${delay}ms` }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: "Gestion des copropriétaires",
      description: "Gérez facilement tous les membres, leurs lots et tantièmes en quelques clics."
    },
    {
      icon: Calendar,
      title: "Assemblées générales",
      description: "Organisez vos AG, envoyez les convocations et générez les PV automatiquement."
    },
    {
      icon: CreditCard,
      title: "Appels de fonds",
      description: "Créez et suivez les appels de fonds avec calcul automatique selon les tantièmes."
    },
    {
      icon: FileText,
      title: "Coffre-fort documentaire",
      description: "Centralisez tous vos documents : PV, contrats, factures, en toute sécurité."
    },
    {
      icon: Scale,
      title: "Aide juridique IA",
      description: "Posez vos questions juridiques à notre assistant IA spécialisé en copropriété."
    },
    {
      icon: Calendar,
      title: "Calendrier partagé",
      description: "Visualisez toutes les échéances : AG, travaux, paiements sur un calendrier."
    }
  ];

  const testimonials = [
    {
      name: "Marie D.",
      role: "Présidente de copropriété",
      content: "Enfin une solution simple et gratuite ! J'ai pu organiser notre AG en 30 minutes au lieu de plusieurs heures.",
      rating: 5
    },
    {
      name: "Jean-Pierre L.",
      role: "Syndic bénévole",
      content: "Les appels de fonds automatiques m'ont fait gagner un temps fou. Plus d'erreurs de calcul !",
      rating: 5
    },
    {
      name: "Sophie M.",
      role: "Conseil syndical",
      content: "On a enfin repris le contrôle de notre copropriété. Fini la dépendance aux syndics hors de prix !",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "L'application est vraiment 100% gratuite ?",
      answer: "Oui ! Toutes les fonctionnalités de gestion sont gratuites : copropriétaires, AG, appels de fonds, documents, assistant juridique. Seuls les envois recommandés (convocations AR, mises en demeure) sont facturés à l'acte car ils utilisent des services postaux externes."
    },
    {
      question: "Comment créer un compte copropriété ?",
      answer: "Inscrivez-vous en 2 minutes, créez votre copropriété et invitez vos copropriétaires. C'est immédiat et sans engagement."
    },
    {
      question: "Quand ai-je besoin d'une convocation en recommandé ?",
      answer: "Pour les AG avec travaux importants ou en cas de désaccord entre copropriétaires, une convocation par lettre recommandée protège contre toute contestation. C'est la garantie que personne ne pourra annuler vos décisions."
    },
    {
      question: "Comment fonctionne le recouvrement des impayés ?",
      answer: "Les relances par email à J+15 et J+30 sont gratuites. Si un copropriétaire ne paie toujours pas, vous pouvez envoyer une mise en demeure en recommandé électronique — c'est la procédure légale obligatoire avant toute action en justice."
    },
    {
      question: "L'aide juridique copropriété est-elle fiable ?",
      answer: "Notre assistant IA est formé sur le droit de la copropriété français. Il vous aide à comprendre la loi et les bonnes pratiques, mais ne remplace pas un avocat pour les cas complexes."
    },
    {
      question: "Mes données sont-elles sécurisées ?",
      answer: "Vos données sont chiffrées et hébergées en France. Nous respectons le RGPD et ne partageons jamais vos informations."
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Helmet>
        <title>Logiciel Syndic GRATUIT | Application Copropriété 100% Gratuite | copropriete.app</title>
        <meta name="description" content="🏠 Logiciel syndic bénévole 100% GRATUIT. Application copropriété gratuite pour gérer vos AG, appels de fonds, comptabilité. Sans abonnement, sans frais cachés. Le seul logiciel de gestion copro vraiment gratuit en France." />
        <meta name="keywords" content="syndic gratuit, logiciel syndic gratuit, copropriété gratuite, application copropriété gratuite, logiciel copropriété gratuit, gestion copro gratuite, syndic bénévole gratuit, outil syndic gratuit, logiciel gestion copropriété gratuit, application syndic gratuite, copro gratuite, syndic benevole gratuit, logiciel copro gratuit, gestion copropriété gratuite, appel de fonds gratuit, assemblée générale gratuit" />
        <meta property="og:title" content="Logiciel Syndic GRATUIT | Copropriété 100% Gratuite" />
        <meta property="og:description" content="🏠 Le SEUL logiciel syndic bénévole vraiment GRATUIT. Gérez votre copropriété sans payer : AG, appels de fonds, comptabilité. 0€ pour toujours." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://copropriete.app" />
        <meta property="og:site_name" content="copropriete.app - Syndic Gratuit" />
        <link rel="canonical" href="https://copropriete.app" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Logiciel Syndic GRATUIT - Application Copropriété 100% Gratuite",
            "description": "Logiciel syndic bénévole 100% GRATUIT. La seule application copropriété vraiment gratuite en France.",
            "url": "https://copropriete.app",
            "mainEntity": {
              "@type": "SoftwareApplication",
              "name": "copropriete.app - Syndic Gratuit",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR",
                "description": "100% GRATUIT - Sans abonnement"
              }
            }
          })}
        </script>
      </Helmet>
      
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <img 
                src={LOGO_URL} 
                alt="copropriete.app" 
                className="w-10 h-10 rounded-xl group-hover:scale-110 transition-transform duration-300"
              />
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 transition-colors">Blog</Link>
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Fonctionnalités</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 hover:text-slate-900">
                Connexion
              </Button>
              <Button onClick={() => navigate('/login')} className="bg-emerald-600 hover:bg-emerald-700 text-white hidden sm:flex">
                <Gift className="w-4 h-4 mr-2" />
                C'est gratuit
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section avec photo de réunion en fond */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Image de fond - réunion de syndic */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/efac59baca9d8b24_R%C3%A9union%20autour%20de%20copropriete.app.png"
            alt="app de copro gratuite"
            className="w-full h-full object-cover"
          />
          {/* Overlay diagonal - blanc à gauche pour texte, transparent à droite pour photo */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative w-full">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-left max-w-2xl"
          >
            {/* Badge gratuit */}
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-2.5 rounded-full text-sm font-bold mb-6 shadow-lg shadow-emerald-500/30">
              <Gift className="w-5 h-5" />
              100% GRATUIT — Sans abonnement, sans engagement
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-tight mb-6">
              Logiciel Syndic <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600">100% GRATUIT</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-slate-700 mb-4">
              La seule application copropriété <span className="bg-emerald-500 text-white px-2 py-1 rounded font-semibold">vraiment gratuite</span> — sans abonnement, sans frais cachés.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8">
              AG, appels de fonds, comptabilité, impayés — Tout est inclus <strong>gratuitement</strong>. Rejoignez les <strong>500+ copropriétés</strong> qui gèrent sans payer.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transition-all hover:scale-105"
              >
                <Unlock className="w-5 h-5 mr-2" />
                Commencer gratuitement
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 rounded-xl border-2 border-slate-300 hover:bg-white/80 transition-all bg-white/70 backdrop-blur-sm"
                onClick={() => document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                Voir la démo
              </Button>
            </motion.div>
            <motion.div variants={fadeInUp} className="mt-8 flex flex-wrap gap-4 text-sm text-slate-700">
              <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span><strong>Gratuit</strong> pour toujours</span>
              </div>
              <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span>Aucune carte bancaire</span>
              </div>
              <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span>Copropriétés illimitées</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <AnimatedSection variants={staggerContainer} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {[
              { value: "100%", label: "Gratuit" },
              { value: "500+", label: "Copropriétés" },
              { value: "4.8/5", label: "Note moyenne" },
              { value: "5 min", label: "Pour démarrer" }
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                variants={scaleIn}
                className="text-center group hover:scale-110 transition-transform cursor-default bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-sm"
              >
                <div className="text-3xl sm:text-4xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">{stat.value}</div>
                <div className="text-slate-600 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="demo-video" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Play className="w-4 h-4" />
              Présentation en vidéo
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Découvrez l'application en <span className="text-emerald-600">2 minutes</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Gary, fondateur de copropriete.app, vous montre comment reprendre le pouvoir sur votre copropriété.
            </p>
          </AnimatedSection>
          
          <AnimatedSection variants={scaleIn} className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 border border-slate-200">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-20 blur-xl -z-10"></div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/u9O6i02zQT4?rel=0&modestbranding=1"
                  title="Présentation copropriete.app - Démonstration"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-500" />
                <span>Gratuit, vraiment</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-500" />
                <span>Mise en place en 5 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-emerald-500" />
                <span>Fait pour les copropriétaires</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Social Proof - Community Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <AnimatedSection variants={fadeInUp}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-200/50 to-emerald-200/50 rounded-3xl blur-2xl"></div>
                <img 
                  src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/efac59baca9d8b24_R%C3%A9union%20autour%20de%20copropriete.app.png"
                  alt="app de copro gratuite - réunion de copropriétaires"
                  className="relative rounded-2xl shadow-xl w-full h-auto object-cover"
                />
              </div>
            </AnimatedSection>
            
            <AnimatedSection variants={fadeInUp} className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                Rejoignez une communauté de <span className="text-emerald-600">copropriétaires épanouis</span>
              </h2>
              <p className="text-lg text-slate-600">
                Fini le stress des AG mal organisées et des impayés qui traînent. 
                Avec copropriete.app, les réunions deviennent productives et la gestion devient un plaisir.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Des voisins qui communiquent</h4>
                    <p className="text-slate-500 text-sm">Tous les copropriétaires ont accès aux mêmes informations en temps réel.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Une gestion sereine</h4>
                    <p className="text-slate-500 text-sm">Plus de conflits liés au manque de transparence. Tout est clair et accessible.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Des économies concrètes</h4>
                    <p className="text-slate-500 text-sm">En moyenne 2000€/an économisés par rapport à un syndic professionnel.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Why Free Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Pourquoi c'est gratuit ?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Notre mission : <strong>démocratiser la gestion de copropriété</strong>. 
              Les syndics professionnels facturent des fortunes pour des services que vous pouvez faire vous-même.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Unlock className="w-8 h-8 mb-3" />
                <h3 className="font-bold text-lg mb-2">Gestion 100% gratuite</h3>
                <p className="text-emerald-100 text-sm">AG, appels de fonds, documents, assistant juridique... Tout est inclus, sans limite.</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Mail className="w-8 h-8 mb-3" />
                <h3 className="font-bold text-lg mb-2">Services postaux à l'acte</h3>
                <p className="text-emerald-100 text-sm">Seuls les recommandés (convocations AR, mises en demeure) sont facturés car ils utilisent La Poste.</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Heart className="w-8 h-8 mb-3" />
                <h3 className="font-bold text-lg mb-2">Pas de piège</h3>
                <p className="text-emerald-100 text-sm">Pas d'abonnement caché, pas de fonctionnalités bridées. On gagne uniquement sur les envois postaux.</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Marre de la gestion <span className="text-red-400">chaotique</span> ?
            </h2>
            <p className="text-xl text-slate-300">Ces problèmes vous parlent ? On les résout — gratuitement.</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { emoji: "📋", title: "Paperasse interminable", desc: "Des heures à préparer les AG, calculer les tantièmes, rédiger les PV..." },
              { emoji: "💸", title: "Impayés difficiles à suivre", desc: "Qui a payé ? Qui doit quoi ? Les relances sont un cauchemar." },
              { emoji: "📊", title: "Excel à ses limites", desc: "30 fichiers Excel, 30 copropriétés... Impossible de s'y retrouver." },
              { emoji: "⚖️", title: "Questions juridiques", desc: "La loi copropriété est complexe. Pas d'avocat sous la main." }
            ].map((problem, i) => (
              <AnimatedSection 
                key={i} 
                variants={fadeInUp}
                delay={i * 100}
                className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800/80 transition-all duration-300"
              >
                <div className="text-3xl mb-3">{problem.emoji}</div>
                <h3 className="text-lg font-bold mb-2">{problem.title}</h3>
                <p className="text-slate-400 text-sm">{problem.desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Gift className="w-4 h-4" />
              Tout ça, gratuit
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tout ce dont vous avez besoin, <span className="text-emerald-600">sans payer</span>
            </h2>
            <p className="text-xl text-slate-600">Des fonctionnalités complètes pour reprendre le contrôle</p>
          </AnimatedSection>
          
          {/* App Screenshots Carousel */}
          <AnimatedSection variants={scaleIn} className="mb-20">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 rounded-3xl blur-3xl"></div>
              <div className="relative bg-gradient-to-b from-slate-50 to-white rounded-3xl p-4 md:p-8 border border-slate-100">
                {/* Carrousel de screenshots - HD images */}
                <div className="overflow-x-auto scrollbar-hide pb-4">
                  <div className="flex gap-4 md:gap-6 min-w-max px-4">
                    {[
                      "https://play-lh.googleusercontent.com/hmfOIYge-gYuPakfBqCmp8VPghUMXsHXKyaTp5z6O9GP0PJuiv1KUA88lhZKmCH3hUigw1_9NdigM3B0nkyeOFc=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/DJpCjpyWLWj3qwi_AVnQYBFTcVOhlSQZBat1PZPT89YVBgVoAs8fEvvAEcw1VpiTe8CT1GIcSYTcum6US2Zjs5E=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/dJVAC167ARj9Ry9Ijjwazj1E8ZzuOIvDvm4j6u2-sQ4-dTICjGH9QPkBOnlLwYtv6vzenPuNtCqUvgsxkFF2Ag=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/7Xps9gmmci8yFFfuk9dGf-EeAlZNij-g9nCZQOa2G_rFtdz-NLDBpxxAWM4UAVPF1OPNXgIciRukeQFawn3a5w=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/oNlZGVHVtpJcoe-H6274l0NV6jBjg4n7zaTpHblw-gbCVdg0wL5Hom7V2fg0yGKDljZby5WnO7_WrIWe12g6=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/Ogu5PFUA0e0t9YH3_izQMn-jSI5fjOWjuUTuF4Fuphs0Lrevoa9gbtCXgRK0Pg0L75bjdB7KbYbQRfyy4G_-SQ0=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/im1Ju2pnhxmWA5iGcnLpXqmgmDBcx3E70tV41Ab9zSBFAVbxWY94mYzLitPW057u3nztD2lA0tlY7AfVKJeZY98=w1052-h592-rw",
                      "https://play-lh.googleusercontent.com/4rYK4EKMciDJjgd9mBrHzLBOcV1e-q0HDY3f0vix16tbtagBQhxJ3eY-zVlLk1xd245pqvdUq-_RBQ14c5D6=w1052-h592-rw"
                    ].map((src, i) => (
                      <div key={i} className="flex-shrink-0">
                        <img 
                          src={src} 
                          alt={`Capture d'écran ${i + 1}`}
                          className="w-72 md:w-96 h-auto rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {/* Indicateur de scroll */}
                <div className="flex justify-center gap-2 mt-4">
                  <span className="text-slate-400 text-sm flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 animate-pulse" />
                    Glissez pour voir plus
                  </span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-slate-600 text-lg mb-4">
                    <span className="font-semibold text-slate-900">Interface intuitive</span> — Gérez tout depuis votre smartphone ou ordinateur
                  </p>
                  {/* Badge Google Play officiel */}
                  <a 
                    href="https://play.google.com/store/apps/details?id=app.copropriete.twa" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block hover:scale-105 transition-transform"
                  >
                    <img 
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/fr_badge_web_generic.png"
                      alt="Disponible sur Google Play"
                      className="h-16 md:h-20"
                    />
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <AnimatedSection 
                key={i}
                variants={scaleIn}
                delay={i * 80}
                className="group p-8 rounded-2xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-500 group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded">GRATUIT</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ils ont repris le pouvoir
            </h2>
            <p className="text-xl text-slate-600">Des copropriétaires libérés des syndics hors de prix</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <AnimatedSection 
                key={i}
                variants={slideFromLeft}
                delay={i * 150}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 text-lg italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-slate-500 text-sm">{testimonial.role}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <AnimatedSection key={i} variants={fadeInUp} delay={i * 50}>
                <details className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="font-semibold text-slate-900 pr-4">{faq.question}</h3>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 pb-6 text-slate-600">
                    {faq.answer}
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Gift className="w-5 h-5" />
              C'est gratuit, vraiment
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Prêt à reprendre le contrôle ?
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              Rejoignez les centaines de copropriétés qui ont dit stop aux syndics hors de prix.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-white text-emerald-700 hover:bg-emerald-50 text-lg px-10 py-6 rounded-xl font-bold"
                >
                  <Unlock className="w-5 h-5 mr-2" />
                  Commencer maintenant — C'est gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </div>
            <p className="mt-6 text-emerald-200 text-sm">
              Pas de carte bancaire • Pas d'engagement • Configuration en 5 minutes
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4 group">
                <img 
                  src={LOGO_URL} 
                  alt="copropriete.app" 
                  className="w-10 h-10 rounded-xl group-hover:scale-110 transition-transform"
                />
                <span className="text-xl font-bold text-white">copropriete.app</span>
              </Link>
              <p className="text-sm">
                L'application gratuite qui redonne le pouvoir aux copropriétés.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Produit</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Fonctionnalités</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Ressources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/syndic-benevole" className="hover:text-white transition-colors">Syndic bénévole</Link></li>
                <li><Link to="/assemblee-generale-copropriete" className="hover:text-white transition-colors">Assemblée générale</Link></li>
                <li><Link to="/charges-copropriete" className="hover:text-white transition-colors">Charges copropriété</Link></li>
                <li><Link to="/appel-de-fonds-modele" className="hover:text-white transition-colors">Appel de fonds</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:gmpdeveloppement@gmail.com" className="hover:text-white transition-colors">gmpdeveloppement@gmail.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2026 copropriete.app - GMP Developpement. Tous droits réservés.</p>
            <div className="flex gap-6 text-sm">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white transition-colors">CGU</Link>
              <Link to="/politique-confidentialite" className="hover:text-white transition-colors">Confidentialité</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
